import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, vModelText as _vModelText, withModifiers as _withModifiers, withKeys as _withKeys, createElementVNode as _createElementVNode, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, vShow as _vShow, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, createSlots as _createSlots, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "combobox" }
const _hoisted_2 = ["id", "placeholder", "aria-invalid", "aria-describedby", "aria-activedescendant"]
const _hoisted_3 = ["aria-label"]
const _hoisted_4 = ["id", "aria-selected", "onClick", "onKeyup"]

import TextField from '@src/components/shared/form_fields/TextField.vue';
import { computed, inject, nextTick, onMounted, ref, useSlots, watch } from 'vue';
import { joinStreetAndBuildingNumber, splitStreetAndBuildingNumber } from '@src/util/street_and_building_number_tools';
import { useAriaDescribedby } from '@src/components/shared/composables/useAriaDescribedby';
import { NullStreetAutocompleteResource } from '@src/api/StreetAutocompleteResource';
import type { StreetAutocompleteResource } from '@src/api/StreetAutocompleteResource';
import { useStreetsResource } from '@src/components/shared/form_fields/useStreetsResource';
import { updateAutocompleteScrollPosition } from '@src/components/shared/form_fields/updateAutocompleteScrollPosition';
import { autoscrollMaxWidth, useAutocompleteScrollIntoViewOnFocus } from '@src/components/shared/form_fields/useAutocompleteScrollIntoViewOnFocus';
import FieldContainer from '@src/components/patterns/FieldContainer.vue';
import { useValueEqualsPlaceholderWarning } from '@src/components/shared/composables/useValueEqualsPlaceholderWarning';
import { useI18n } from 'vue-i18n';

enum InteractionState {
	Typing,
	Selecting,
}

interface Props {
	inputIdStreetName: string;
	inputIdBuildingNumber: string;
	isInline?: boolean;
	scrollTargetId: string;
	modelValue: string;
	showError: boolean;
	postcode: string;
	isMaxWidthField?: boolean;
	ariaDescribedby?: string | undefined;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'StreetAutocompleteField',
  props: {
    inputIdStreetName: {},
    inputIdBuildingNumber: {},
    isInline: { type: Boolean },
    scrollTargetId: {},
    modelValue: {},
    showError: { type: Boolean },
    postcode: {},
    isMaxWidthField: { type: Boolean },
    ariaDescribedby: {}
  },
  emits: [ 'update:modelValue', 'field-changed' ],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;
const { t } = useI18n();
const slots = useSlots();

const streetNameModel = ref<string>( '' );
const buildingNumberModel = ref<string>( '' );
const buildingNumberWasBlurred = ref<boolean>( false );
const showBuildingNumberWarning = computed( () => streetNameModel.value !== '' && buildingNumberWasBlurred.value && buildingNumberModel.value === '' );
const autocompleteIsActive = ref<Boolean>( false );
const { streets, fetchStreetsForPostcode } = useStreetsResource( inject<StreetAutocompleteResource>( 'streetAutocompleteResource', NullStreetAutocompleteResource ) );
const activeStreet = ref<string>();
const interactionState = ref<InteractionState>( InteractionState.Typing );
const scrollElement = ref<HTMLElement>();
const valueEqualsPlaceholderWarning = useValueEqualsPlaceholderWarning( streetNameModel, t( 'donation_form_street_name_placeholder' ), 'donation_form_street_placeholder_warning' );
const ariaDescribedby = useAriaDescribedby(
	props.inputIdStreetName,
	computed<boolean>( () => false ),
	computed<boolean>( () => props.showError ),
	computed<boolean>( () => valueEqualsPlaceholderWarning.hasWarning.value || !!slots.message ),
	computed<string | undefined>( () => props.ariaDescribedby )
);
const scrollIntoView = useAutocompleteScrollIntoViewOnFocus( props.scrollTargetId, autoscrollMaxWidth );

const filteredStreets = computed<Array<string>>( () => {
	const streetList = streets.value.filter( ( streetItem: string ) => {
		return streetItem
			.toLowerCase()
			.startsWith( streetNameModel.value.trim().toLowerCase() );
	} );

	return streetNameModel.value !== '' || streetList.length > 0 ? streetList : streets.value;
} );
const activeStreetId = computed<number>( () => filteredStreets.value.indexOf( activeStreet.value ) );

const onUpdateModel = (): void => {
	emit( 'update:modelValue', joinStreetAndBuildingNumber( streetNameModel.value, buildingNumberModel.value ) );
};

const onStreetNameFocus = ( event: Event ) => {
	autocompleteIsActive.value = true;
	scrollIntoView();
	( event.target as HTMLInputElement ).select();
};

const onStreetNameKeydown = ( event: KeyboardEvent ) => {
	if ( [ 'ArrowUp', 'ArrowDown', 'Tab', 'Enter' ].includes( event.key ) ) {
		return true;
	}

	interactionState.value = InteractionState.Typing;
	activeStreet.value = undefined;
	updateAutocompleteScrollPosition( scrollElement );
};

const onStreetNameKeyArrows = async ( direction: 'up' | 'down' ) => {
	interactionState.value = InteractionState.Selecting;

	if ( activeStreet.value === undefined ) {
		activeStreet.value = filteredStreets.value[ 0 ];
		return;
	}

	let index = filteredStreets.value.findIndex( x => x === activeStreet.value );

	if ( direction === 'up' && index > 0 ) {
		index--;
	}

	if ( direction === 'down' && index + 1 < filteredStreets.value.length ) {
		index++;
	}

	activeStreet.value = filteredStreets.value[ index ];

	await nextTick();
	updateAutocompleteScrollPosition( scrollElement );
};

const onStreetNameKeySubmit = () => {
	if ( interactionState.value === InteractionState.Typing ) {
		return;
	}

	streetNameModel.value = activeStreet.value;
};

let itemWasJustSelectedFromList = false;

const onStreetNameBlur = () => {
	setTimeout( () => {
		autocompleteIsActive.value = false;
		if ( !itemWasJustSelectedFromList ) {
			emit( 'field-changed' );
		}
		itemWasJustSelectedFromList = false;
	}, 200 );
};

const onStreetNameInput = (): void => {
	if ( props.showError ) {
		emit( 'field-changed' );
	}
};

const onSelectStreet = async ( newStreet: string ) => {
	itemWasJustSelectedFromList = true;
	streetNameModel.value = newStreet;
	await nextTick();
	emit( 'field-changed' );
};

const setModelValues = ( newValue: string ) => {
	const values = splitStreetAndBuildingNumber( newValue );
	streetNameModel.value = values.street;
	buildingNumberModel.value = values.buildingNumber;
};

onMounted( () => {
	setModelValues( props.modelValue );
	fetchStreetsForPostcode( props.postcode );
} );
watch( () => props.modelValue, ( newValue: string ) => setModelValues( newValue ) );
watch( () => props.postcode, ( value: string ) => fetchStreetsForPostcode( value ) );
watch( streetNameModel, onUpdateModel );

const onBuildingNumberBlur = (): void => {
	buildingNumberWasBlurred.value = true;
	emit( 'field-changed' );
};


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass({ 'flow': !__props.isInline, 'flex-field-group' : __props.isInline })
  }, [
    _createVNode(FieldContainer, {
      "input-id": __props.inputIdStreetName,
      "show-error": __props.showError,
      "is-max-width-field": __props.isMaxWidthField,
      class: "flex-field-group__sidebar-field"
    }, _createSlots({
      label: _withCtx(() => [
        _createTextVNode(_toDisplayString(_ctx.$t( 'donation_form_street_name_label' )), 1)
      ]),
      field: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _withDirectives(_createElementVNode("input", {
            type: "text",
            name: "street",
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((streetNameModel).value = $event)),
            id: __props.inputIdStreetName,
            autocomplete: "street-address",
            placeholder: _ctx.$t( 'form_for_example', { example: _ctx.$t( 'donation_form_street_name_placeholder' ) } ),
            "aria-controls": "streets",
            "aria-invalid": __props.showError,
            "aria-describedby": _unref(ariaDescribedby),
            "aria-autocomplete": "list",
            "aria-activedescendant": activeStreet.value ? `street-${activeStreetId.value}` : null,
            onFocus: onStreetNameFocus,
            onBlur: onStreetNameBlur,
            onInput: onStreetNameInput,
            onKeydown: [
              onStreetNameKeydown,
              _cache[1] || (_cache[1] = _withKeys(_withModifiers(($event: any) => (onStreetNameKeyArrows('up')), ["prevent"]), ["up"])),
              _cache[2] || (_cache[2] = _withKeys(_withModifiers(($event: any) => (onStreetNameKeyArrows('down')), ["prevent"]), ["down"])),
              _withKeys(onStreetNameKeySubmit, ["tab"]),
              _withKeys(onStreetNameKeySubmit, ["enter"])
            ]
          }, null, 40, _hoisted_2), [
            [_vModelText, streetNameModel.value]
          ]),
          _createVNode(_Transition, { name: "fade" }, {
            default: _withCtx(() => [
              _withDirectives(_createElementVNode("div", {
                id: "streets",
                ref_key: "scrollElement",
                ref: scrollElement,
                tabindex: "-1",
                role: "listbox",
                "aria-label": _ctx.$t( 'donation_form_street_name_list_label' )
              }, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredStreets.value, (street, index) => {
                  return (_openBlock(), _createElementBlock("button", {
                    key: street,
                    tabindex: "-1",
                    role: "option",
                    id: `street-${index}`,
                    "aria-selected": street === activeStreet.value,
                    onClick: ($event: any) => (onSelectStreet( street )),
                    onKeyup: _withKeys(($event: any) => (onSelectStreet( street )), ["enter","space"])
                  }, _toDisplayString(street), 41, _hoisted_4))
                }), 128))
              ], 8, _hoisted_3), [
                [_vShow, autocompleteIsActive.value && filteredStreets.value.length > 0]
              ])
            ]),
            _: 1
          })
        ])
      ]),
      error: _withCtx(() => [
        _createTextVNode(_toDisplayString(_ctx.$t( 'donation_form_street_error' )), 1)
      ]),
      _: 2
    }, [
      (_unref(valueEqualsPlaceholderWarning).hasWarning.value)
        ? {
            name: "message",
            fn: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(valueEqualsPlaceholderWarning).warning), 1)
            ]),
            key: "0"
          }
        : undefined
    ]), 1032, ["input-id", "show-error", "is-max-width-field"]),
    _createVNode(TextField, {
      name: "building-number",
      "input-id": __props.inputIdBuildingNumber,
      class: _normalizeClass({ 'flex-field-group__sidebar-field-sidebar' : __props.isInline }),
      modelValue: buildingNumberModel.value,
      "onUpdate:modelValue": [
        _cache[3] || (_cache[3] = ($event: any) => ((buildingNumberModel).value = $event)),
        onUpdateModel
      ],
      "show-error": false,
      "error-message": _ctx.$t('donation_form_building_number_error'),
      label: _ctx.$t( __props.isInline ? 'donation_form_building_number_label_short' : 'donation_form_building_number_label' ),
      placeholder: _ctx.$t( 'donation_form_building_number_placeholder' ),
      onFieldChanged: onBuildingNumberBlur,
      "is-max-width-field": __props.isMaxWidthField,
      "aria-describedby": props.ariaDescribedby
    }, _createSlots({ _: 2 }, [
      (showBuildingNumberWarning.value)
        ? {
            name: "message",
            fn: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t( 'donation_form_street_number_warning' )), 1)
            ]),
            key: "0"
          }
        : undefined
    ]), 1032, ["input-id", "class", "modelValue", "error-message", "label", "placeholder", "is-max-width-field", "aria-describedby"])
  ], 2))
}
}

})